import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { FileText, Search, ArrowLeft, ChevronRight } from "lucide-react";
import { PageHero } from "./FormKit";

// Reusable documentation hub + inline reader for a set of docs.
// docs: [{ slug, title, num?, body: [paragraphs] }]
export const DocHub = ({ eyebrow, title, subtitle, docs, basePath, activeSlug, footerNote }) => {
  const [q, setQ] = useState("");
  const active = useMemo(() => docs.find((d) => d.slug === activeSlug), [docs, activeSlug]);
  const filtered = useMemo(
    () => docs.filter((d) => d.title.toLowerCase().includes(q.toLowerCase())),
    [docs, q]
  );

  return (
    <div data-testid="doc-hub">
      <PageHero eyebrow={eyebrow} title={active ? active.title : title} subtitle={active ? undefined : subtitle} />
      <section className="px-4 md:px-8 lg:px-12 mx-auto max-w-7xl py-10 grid lg:grid-cols-[300px_1fr] gap-8">
        <aside className="lg:sticky lg:top-28 h-fit">
          <div className="flex items-center gap-2 bg-white border border-black/15 px-3 h-11 mb-4">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input data-testid="doc-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search documents..." className="bg-transparent outline-none text-sm w-full" />
          </div>
          <nav className="border border-black/10 bg-white divide-y divide-black/5 max-h-[65vh] overflow-y-auto">
            {filtered.map((d) => (
              <Link key={d.slug} to={`${basePath}/${d.slug}`} data-testid={`doc-link-${d.slug}`}
                className={`flex items-center justify-between gap-2 px-4 py-3 text-sm hover:bg-taven-gray/50 transition-colors ${active?.slug === d.slug ? "bg-taven-gray font-bold text-taven-gold" : ""}`}>
                <span className="flex items-center gap-2">{d.num && <span className="text-[10px] font-bold text-muted-foreground w-8 shrink-0">{d.num}</span>}<span className="line-clamp-1">{d.title}</span></span>
                <ChevronRight className="w-3.5 h-3.5 shrink-0 opacity-40" />
              </Link>
            ))}
          </nav>
        </aside>

        <div className="bg-white border border-black/10 p-6 md:p-10 min-h-[50vh]">
          {active ? (
            <article data-testid="doc-content">
              <Link to={basePath} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-black mb-6"><ArrowLeft className="w-3.5 h-3.5" /> All Documents</Link>
              <h2 className="text-2xl md:text-3xl font-black font-display tracking-tight">{active.num ? `${active.num}. ` : ""}{active.title}</h2>
              <div className="mt-5 space-y-4">
                {active.body.map((para) => <p key={para.slice(0, 28)} className="text-muted-foreground leading-relaxed">{para}</p>)}
              </div>
              {footerNote && <p className="text-xs text-muted-foreground border-t border-black/10 pt-4 mt-8">{footerNote}</p>}
            </article>
          ) : (
            <div>
              <p className="text-sm text-muted-foreground mb-6">Select a document from the index, or browse the full library below.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {docs.map((d) => (
                  <Link key={d.slug} to={`${basePath}/${d.slug}`} className="p-4 border border-black/5 hover:border-taven-gold hover-lift flex items-start gap-3 group">
                    <FileText className="w-5 h-5 text-taven-gold shrink-0 mt-0.5" />
                    <span className="text-sm font-semibold group-hover:text-taven-gold">{d.num ? `${d.num}. ` : ""}{d.title}</span>
                  </Link>
                ))}
              </div>
              {footerNote && <p className="text-xs text-muted-foreground border-t border-black/10 pt-4 mt-8">{footerNote}</p>}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default DocHub;
